// src/pages/TermsPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { FileText, AlertCircle, Users, Shield, Gavel, Mail, Copyright, Ban, RefreshCw, AlertTriangle, DollarSign, Scale, MapPin, FileWarning, UserX } from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const TermsPage = () => {
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Terms of Service for HousingAI (The Company)
              </h1>
              <p className="text-xl text-gray-600">
                Last updated: September 05, 2025
              </p>
            </motion.div>

            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <p className="text-gray-700 leading-relaxed">
                The "Service" provided by HousingAI is a multilingual, AI-powered chat assistant available 24/7 to help 
                the general public, subsidized housing applicants, and subsidized housing tenants easily find answers to 
                frequently asked questions about subsidized housing programs and services.
              </p>
              <p className="text-gray-700 mt-4">
                By accessing or using the Housing-Authority Chat Widget service ("the Service"), you agree to be bound by 
                these Terms of Service as well as our associated Privacy Notice. If you do not agree to these Terms, 
                do not use the Service.
              </p>
            </motion.div>

            {/* Section 1 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-8 w-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
              </div>
              <p className="text-gray-700 mb-4">
                You represent that you are at least 18 years old, or if you are between 13-17 years old, that you have 
                the consent of a parent or legal guardian to use the Service and accept these Terms.
              </p>
              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-xl">
                <p className="text-gray-800">
                  <strong>Children under 13 are not permitted to use this Service without verified parental consent 
                  in compliance with COPPA.</strong>
                </p>
              </div>
            </motion.div>

            {/* Section 2 - OpenAI Warning */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="h-8 w-8 text-orange-600" />
                <h2 className="text-2xl font-bold text-gray-900">2. AI-Powered Service & Disclaimer</h2>
              </div>
              <p className="text-gray-700 mb-4">
                The Service is powered by an artificial intelligence system (specifically, the OpenAI API) that generates 
                responses to your questions automatically. Responses are not reviewed or verified by a human before you 
                see them. You acknowledge that AI-generated answers may sometimes be incomplete, incorrect, or misleading.
              </p>
              
              {/* OpenAI Data Processing Warning */}
              <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-6 mb-4">
                <h3 className="text-lg font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <FileWarning className="h-6 w-6" />
                  OPENAI DATA PROCESSING WARNING
                </h3>
                <p className="text-gray-700 mb-3">By using this Service, you understand and accept that:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">All messages you type are sent directly to OpenAI's servers for processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">OpenAI retains your data for 30 days as part of their standard API operations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">This retention is mandatory and we cannot opt out of it</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">
                      Your data is subject to OpenAI's data usage policies:{' '}
                      <a href="https://openai.com/policies/api-data-usage-policies" className="text-blue-600 hover:underline break-all">
                        https://openai.com/policies/api-data-usage-policies
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">OpenAI may update their policies at any time without notice</span>
                  </li>
                </ul>
                <p className="text-orange-800 font-bold mt-3">
                  If you have not read OpenAI's terms and policies, consider this your warning that your data will be 
                  retained by them for 30 days
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-xl">
                <p className="text-gray-800">
                  <strong>No Professional Advice:</strong> The information and answers provided by the Service do not 
                  constitute legal, financial, medical, housing, or any other professional advice. Always seek the advice 
                  of qualified human professionals for specific questions about your personal situation. We are not 
                  responsible for any actions you take based on the Service's responses.
                </p>
              </div>
            </motion.div>

            {/* Section 3 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Users className="h-8 w-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">3. User Conduct</h2>
              </div>
              <p className="text-gray-700 mb-4">
                When using the Service, you agree to conduct yourself responsibly and refrain from misuse. You may not:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <div>
                    <strong>Use the Service for any unlawful, fraudulent, or harmful purpose.</strong> This includes 
                    violating any local, state, national, or international laws while using the widget.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <div>
                    <strong>Attempt to reverse-engineer, decompile, tamper with, or otherwise exploit</strong> the 
                    underlying AI model or any part of the Service. Any attempt to gain unauthorized access to the 
                    services' systems or to probe or test vulnerabilities is strictly prohibited.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <div>
                    <strong>Submit any content that violates third-party rights or contains prohibited material.</strong> 
                    Prohibited content includes hate speech, harassment or threats, defamatory statements, obscene or 
                    pornographic material, or any content that infringes on copyrights, privacy rights, or other 
                    third-party rights.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <div>
                    <strong>Engage in any behavior that could disrupt or interfere with normal operation.</strong> 
                    For example, you may not spam the widget with repetitive prompts, attempt to overload the system, 
                    or use the Service to distribute malware or other harmful code.
                  </div>
                </li>
              </ul>
            </motion.div>

            {/* Section 4 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-8 w-8 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">4. Privacy & Data</h2>
              </div>
              <p className="text-gray-700">
                Your use of the Services is also governed by our{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">Privacy Notice</a>, which describes how we 
                collect and handle your data. By using the chat widget, you consent to the collection, use, and disclosure 
                of information as described in that Privacy Notice.
              </p>
              <p className="text-gray-700 mt-4">
                In summary, we primarily collect the text of your questions and technical metadata, and we do not require 
                personal identifying information for you to use the Service.
              </p>
            </motion.div>

            {/* Section 5 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Copyright className="h-8 w-8 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">5. Intellectual Property</h2>
              </div>
              <p className="text-gray-700">
                All content, software, and materials provided as part of the Service (including but not limited to the AI 
                model's responses, the widget's code, design, and branding) are owned by or licensed to HousingAI. These 
                are protected by intellectual property laws and other applicable laws.
              </p>
              <p className="text-gray-700 mt-4">
                You may not copy, reproduce, distribute, publicly display, create derivative works from, or otherwise exploit 
                any part of the services' content or underlying technology without express prior written permission from us. 
                This includes scraping or extracting data from the Service for any commercial or unauthorized purpose.
              </p>
            </motion.div>

            {/* Section 6 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">6. Disclaimers and Limitation of Liability</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-xl">
                  <p className="text-gray-800">
                    <strong>No Warranties:</strong> The Service is provided "AS IS" and "AS AVAILABLE," without any 
                    warranties of any kind, either express or implied. We disclaim all warranties including implied 
                    warranties of accuracy, reliability, merchantability, fitness for a particular purpose, and 
                    non-infringement.
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-xl">
                  <p className="text-gray-800">
                    <strong>Limitation of Liability:</strong> To the maximum extent permitted by applicable law, HousingAI 
                    and its officers, employees, partners, and affiliates will not be liable for any direct, indirect, 
                    incidental, special, consequential, or punitive damages arising out of or related to your use of 
                    (or inability to use) the Service.
                  </p>
                </div>
                <p className="text-gray-700">
                  This includes, but is not limited to, any errors or omissions in the content, any loss or damage of any 
                  kind (such as loss of data, loss of profits or revenue, business interruption, or personal injuries), 
                  even if we have been advised of the possibility of such damages.
                </p>
              </div>
            </motion.div>

            {/* Section 7 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Scale className="h-8 w-8 text-yellow-600" />
                <h2 className="text-2xl font-bold text-gray-900">7. Indemnification</h2>
              </div>
              <p className="text-gray-700">
                You agree to defend, indemnify, and hold harmless HousingAI, its owners, officers, employees, agents, and 
                affiliates, from and against any and all claims, liabilities, damages, losses, or expenses (including 
                reasonable attorneys' fees and costs) arising out of or in any way connected with:
              </p>
              <ol className="mt-4 space-y-2 ml-6 list-decimal">
                <li className="text-gray-700">your use or misuse of the Service;</li>
                <li className="text-gray-700">your violation of any portion of these Terms; or</li>
                <li className="text-gray-700">your violation of any third-party rights or applicable law in relation to your use of the Service.</li>
              </ol>
            </motion.div>

            {/* Section 8 - Fees & Payment */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="h-8 w-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">8. Fees & Payment for Organizations</h2>
              </div>
              <p className="text-gray-700 mb-4">
                For individual end-users asking questions through the chat widget, the Service is free of charge – you will 
                not be billed or charged any fee for using the widget on a housing authority's site.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r-xl mb-4">
                <p className="text-gray-800">
                  <strong>For Organizations:</strong> If you represent a housing authority, Company, or other organization 
                  that wishes to deploy or license this chat widget on your own website, a separate subscription or licensing 
                  agreement with us is required.
                </p>
              </div>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Custom Plans</h3>
                  <p className="text-gray-700">
                    We do not offer standard "self-serve" subscription tiers. Instead, pricing and terms are determined on 
                    a case-by-case basis with features and usage limits tailored to your needs.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Contact us for a Quote</h3>
                  <p className="text-gray-700">
                    Organizations interested in using the Service should contact us to discuss pricing and terms. Payment is 
                    typically handled via invoice and traditional payment methods (e.g., check or ACH transfer) rather than 
                    online credit card billing.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Section 9 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Ban className="h-8 w-8 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">9. Suspension & Termination</h2>
              </div>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, 
                for any reason. This includes (but is not limited to) cases where we believe you have violated these Terms, 
                violated applicable laws, or engaged in behavior that is harmful to other users, us, or our systems.
              </p>
              <p className="text-gray-700 mt-4">
                If your access is terminated, you must immediately stop using the Service. Sections of these Terms that by 
                their nature should survive termination will remain in effect even after your access is terminated.
              </p>
            </motion.div>

            {/* Section 10 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <RefreshCw className="h-8 w-8 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">10. Changes to Terms</h2>
              </div>
              <p className="text-gray-700">
                We may revise or update these Terms of Service from time to time. Whenever we make material changes, we will 
                post the updated Terms on this page and update the "Last updated" date at the top. It is your responsibility 
                to review the Terms periodically for any changes.
              </p>
              <p className="text-gray-700 mt-4">
                If you continue to use the Service after changes to the Terms are posted, that will constitute your acceptance 
                of the revised Terms. If you do not agree with the new Terms, you should discontinue use of the Service.
              </p>
            </motion.div>

            {/* Section 11 - Governing Law with Geographic Limitations */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Gavel className="h-8 w-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">11. Governing Law</h2>
              </div>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Massachusetts, 
                USA, without regard to its conflict of law principles. Any dispute arising from or relating to the Service or 
                these Terms that cannot be resolved amicably shall be brought exclusively in the state or federal courts 
                located in Suffolk County, Massachusetts.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-xl mb-4">
                <p className="text-gray-800">
                  <strong>This Service is intended solely for use within the Commonwealth of Massachusetts.</strong> These Terms 
                  and the Service are not applicable to, intended for, or available to users in any other state, U.S. territory, 
                  or country.
                </p>
              </div>
              
              <p className="text-gray-700 mb-3">By accessing the Service from outside Massachusetts, you acknowledge that:</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-1" />
                  <span className="text-gray-700">You do so at your own risk and sole responsibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-1" />
                  <span className="text-gray-700">We make no representations about the Service's compliance with laws outside Massachusetts</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-1" />
                  <span className="text-gray-700">The Service may not function properly or be legally appropriate for use in your jurisdiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-1" />
                  <span className="text-gray-700">You are responsible for ensuring your use complies with all applicable local laws</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                We do not market, promote, or support the use of this Service outside Massachusetts. We reserve the right to 
                restrict or terminate access from any location outside Massachusetts without notice.
              </p>
            </motion.div>

            {/* Section 12 - Miscellaneous */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-8 w-8 text-gray-600" />
                <h2 className="text-2xl font-bold text-gray-900">12. Miscellaneous</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700">
                    <strong>Severability:</strong> If any provision of these Terms is held to be invalid or unenforceable by a 
                    court of competent jurisdiction, that provision will be severed and the remainder of the Terms will remain 
                    in full force and effect.
                  </p>
                </div>
                <div>
                  <p className="text-gray-700">
                    <strong>No Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute 
                    a waiver of that right or provision. No waiver of any term shall be deemed a further or continuing waiver of 
                    that or any other term.
                  </p>
                </div>
                <div>
                  <p className="text-gray-700">
                    <strong>Entire Agreement:</strong> These Terms, together with the Privacy Notice referenced herein, constitute 
                    the entire agreement between you and HousingAI regarding your use of the Service. They supersede any prior 
                    agreements or communications relating to the subject matter.
                  </p>
                </div>
                <div>
                  <p className="text-gray-700">
                    <strong>Assignment:</strong> You may not assign or transfer any rights or obligations under these Terms without 
                    our prior written consent. We may assign or transfer our rights and obligations to an affiliate or in connection 
                    with a merger, acquisition, reorganization, or sale of assets.
                  </p>
                </div>
                <div>
                  <p className="text-gray-700">
                    <strong>Headings:</strong> The section titles in these Terms are for convenience only and have no legal or 
                    contractual effect.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Section 13 - Contact */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="bg-white rounded-3xl p-8 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <Mail className="h-8 w-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">13. Contact Us</h2>
              </div>
              <p className="text-gray-700">
                If you have any questions, concerns, or requests regarding these Terms of Service, you can contact us at:{' '}
                <a href="mailto:legal@gethousingai.com" className="text-blue-600 hover:underline">willem@gethousingai.com</a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default TermsPage;